/**
 * Sitemate/Dashpivot API Service
 *
 * All API calls route through the `dashpivot-proxy` Supabase Edge Function.
 * The proxy handles OAuth2 token acquisition server-side, so NO secrets in the browser.
 * Docs: https://flowsite.com/docs/dashpivot/api
 */

import { supabase } from "./supabase";
import { logError } from "./logError";

// ============================================
// Configuration (NO SECRETS, feature flags only)
// ============================================

/**
 * Whether Dashpivot integration is enabled for this deployment.
 * Controlled by VITE_DASHPIVOT_ENABLED env var per client site.
 */
const DASHPIVOT_ENABLED =
  import.meta.env.VITE_DASHPIVOT_ENABLED === "true" ||
  import.meta.env.VITE_DASHPIVOT_ENABLED === "1";

/**
 * Dashpivot List ID for Job Locations dropdown.
 * Non-sensitive, just a resource identifier.
 */
const DASHPIVOT_LOCATIONS_LIST_ID =
  import.meta.env.VITE_DASHPIVOT_LOCATIONS_LIST_ID || "";

/**
 * Supabase Edge Function proxy URL.
 * All Dashpivot API calls go through this proxy which handles auth server-side.
 */
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";
const PROXY_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/dashpivot-proxy`;

// ============================================
// Public API
// ============================================

/**
 * Get the configured Dashpivot locations list ID.
 */
export function getLocationsListId(): string {
  return DASHPIVOT_LOCATIONS_LIST_ID;
}

/**
 * Check if Dashpivot integration is enabled for this deployment.
 * No longer checks for client secrets, which live server-side only.
 */
export function isDashpivotConfigured(): boolean {
  return DASHPIVOT_ENABLED && Boolean(SUPABASE_URL);
}

/**
 * Get configuration status for debugging (no secrets exposed).
 */
export function getDashpivotConfigStatus(): {
  configured: boolean;
  enabled: boolean;
  hasProxyUrl: boolean;
  hasLocationsListId: boolean;
  proxyUrl: string;
} {
  return {
    configured: isDashpivotConfigured(),
    enabled: DASHPIVOT_ENABLED,
    hasProxyUrl: Boolean(SUPABASE_URL),
    hasLocationsListId: Boolean(DASHPIVOT_LOCATIONS_LIST_ID),
    proxyUrl: PROXY_FUNCTION_URL,
  };
}

// ============================================
// API Types based on OpenAPI spec
// ============================================

export interface DashpivotFolder {
  id: string;
  name: string;
  state: "active" | "archived";
  path: string;
  createdAt: string;
  updatedAt: string;
}

export interface DashpivotList {
  id: string;
  name: string;
  state: "active" | "archived";
  items: DashpivotListItem[];
  createdAt: string;
  updatedAt: string;
}

export interface DashpivotListItem {
  id: string;
  value: string;
  style?: string;
  state?: "active" | "archived";
}

export interface PaginatedResponse<T> {
  data: T[];
  links: {
    next?: string;
  };
}

// ============================================
// Internal: Proxy-based API Request
// ============================================

/**
 * Make API request to Dashpivot via Edge Function proxy.
 * The proxy handles OAuth2 authentication server-side.
 * No secrets are transmitted from the browser.
 */
async function apiRequest<T>(
  endpoint: string,
  options: { method?: string; body?: unknown } = {}
): Promise<T> {
  // Get the current user session for Supabase Edge Function auth.
  // The gateway requires at minimum an apikey; the Bearer JWT also enables
  // per-org credential lookup inside the proxy function.
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "apikey": anonKey,
  };
  if (accessToken) {
    headers["Authorization"] = `Bearer ${accessToken}`;
  }

  const response = await fetch(PROXY_FUNCTION_URL, {
    method: "POST",
    headers,
    body: JSON.stringify({
      endpoint,
      method: options.method || "GET",
      body: options.body,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    logError(`Dashpivot proxy error (status ${response.status})`);
    throw new Error(
      errorData.error || `API request failed: ${response.status}`
    );
  }

  return response.json();
}

// ============================================
// API Methods
// ============================================

/**
 * Get root folders (workspaces)
 */
export async function getFolders(params?: {
  name?: string;
  pageSize?: number;
  pageCursor?: string;
}): Promise<PaginatedResponse<DashpivotFolder>> {
  const searchParams = new URLSearchParams();
  if (params?.name) searchParams.append("name", params.name);
  if (params?.pageSize)
    searchParams.append("pageSize", params.pageSize.toString());
  if (params?.pageCursor) searchParams.append("pageCursor", params.pageCursor);

  const query = searchParams.toString();
  return apiRequest<PaginatedResponse<DashpivotFolder>>(
    `/folders${query ? `?${query}` : ""}`
  );
}

/**
 * Get folder by ID
 */
export async function getFolderById(
  folderId: string
): Promise<{ data: DashpivotFolder }> {
  return apiRequest<{ data: DashpivotFolder }>(`/folders/${folderId}`);
}

/**
 * Get child folders of a folder
 */
export async function getFolderChildren(
  folderId: string,
  params?: {
    name?: string;
    state?: "active" | "archived";
    pageSize?: number;
    pageCursor?: string;
  }
): Promise<PaginatedResponse<DashpivotFolder>> {
  const searchParams = new URLSearchParams();
  if (params?.name) searchParams.append("name", params.name);
  if (params?.state) searchParams.append("state", params.state);
  if (params?.pageSize)
    searchParams.append("pageSize", params.pageSize.toString());
  if (params?.pageCursor) searchParams.append("pageCursor", params.pageCursor);

  const query = searchParams.toString();
  return apiRequest<PaginatedResponse<DashpivotFolder>>(
    `/folders/${folderId}/children${query ? `?${query}` : ""}`
  );
}

/**
 * Get lists in a folder
 */
export async function getListsInFolder(
  folderId: string,
  params?: {
    state?: "active" | "archived";
    pageSize?: number;
    pageCursor?: string;
  }
): Promise<PaginatedResponse<DashpivotList>> {
  const searchParams = new URLSearchParams();
  if (params?.state) searchParams.append("state", params.state);
  if (params?.pageSize)
    searchParams.append("pageSize", params.pageSize.toString());
  if (params?.pageCursor) searchParams.append("pageCursor", params.pageCursor);

  const query = searchParams.toString();
  return apiRequest<PaginatedResponse<DashpivotList>>(
    `/folders/${folderId}/lists${query ? `?${query}` : ""}`
  );
}

/**
 * Get a specific list by ID
 */
export async function getListById(
  listId: string
): Promise<{ data: DashpivotList }> {
  return apiRequest<{ data: DashpivotList }>(`/lists/${listId}`);
}

/**
 * Helper: Get all items from a list
 */
export async function getListItems(
  listId: string
): Promise<DashpivotListItem[]> {
  const response = await getListById(listId);
  return response.data.items || [];
}
